import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "../lib/api";
import Spinner from "../components/Spinner";

// Volunteer adoption readout. Aggregate counts only, by design: this
// answers "are volunteers coming back?" without ever showing who did
// what. There is no per-volunteer view here or anywhere else in the
// product, and nothing leaves our Supabase.
interface AdoptionParish {
  parishId: string;
  parishName: string;
  signedUp: number;
  newSignups: number;
  activated: number;
  activatedPct: number | null;
  returned: number;
  returnEligible: number;
  returnRatePct: number | null;
  scheduleEngaged: number;
  schedulePct: number | null;
  subRequests: number;
  helpUses: number;
}

interface AdoptionPayload {
  windowDays: number;
  scope: "parish" | "all";
  platformAdmin: boolean;
  notMigrated: boolean;
  parishes: AdoptionParish[];
}

const WINDOWS = [7, 30, 90];

export default function Adoption() {
  const [days, setDays] = useState(30);
  const [allParishes, setAllParishes] = useState(false);
  const [data, setData] = useState<AdoptionPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const query = `/adoption?days=${days}${allParishes ? "&scope=all" : ""}`;
    apiGet<AdoptionPayload>(query)
      .then((payload) => {
        if (cancelled) return;
        setData(payload);
        setError(null);
        setLoading(false);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days, allParishes]);

  const totals = useMemo(() => {
    if (!data || data.parishes.length < 2) return null;
    // Cross-parish roll-up: sums for counts, recomputed (never
    // averaged) for rates, so a tiny parish can't swing the number.
    const sum = (pick: (p: AdoptionParish) => number) =>
      data.parishes.reduce((n, p) => n + pick(p), 0);
    const signedUp = sum((p) => p.signedUp);
    const eligible = sum((p) => p.returnEligible);
    const returned = sum((p) => p.returned);
    return {
      signedUp,
      returned,
      eligible,
      returnRatePct: eligible ? Math.round((returned / eligible) * 100) : null,
      subRequests: sum((p) => p.subRequests),
    };
  }, [data]);

  function exportCsv() {
    if (!data) return;
    const header = [
      "parish",
      "window_days",
      "signed_up",
      "new_signups_in_window",
      "activated",
      "activated_pct",
      "returned",
      "return_eligible",
      "return_rate_pct",
      "schedule_engaged",
      "schedule_pct",
      "sub_requests",
      "help_uses",
    ];
    const rows = data.parishes.map((p) => [
      `"${p.parishName.replace(/"/g, '""')}"`,
      data.windowDays,
      p.signedUp,
      p.newSignups,
      p.activated,
      p.activatedPct ?? "",
      p.returned,
      p.returnEligible,
      p.returnRatePct ?? "",
      p.scheduleEngaged,
      p.schedulePct ?? "",
      p.subRequests,
      p.helpUses,
    ]);
    const csv = [header.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `sacristy-adoption-${data.windowDays}d.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <main className="page page--settings">
      <div className="settings__back">
        <Link className="sign-in__link" to="/">
          ← Back to dashboard
        </Link>
      </div>
      <h1 className="settings__title">Volunteer adoption</h1>
      <p className="settings__section-lede">
        Are volunteers coming back on their own? Counts and percentages only, never individual
        activity.
      </p>

      <div className="adoption__controls">
        <div className="adoption__windows" role="group" aria-label="Time window">
          {WINDOWS.map((w) => (
            <button
              key={w}
              type="button"
              className={`adoption__window${w === days ? " is-active" : ""}`}
              onClick={() => setDays(w)}
            >
              Last {w} days
            </button>
          ))}
        </div>
        {data?.platformAdmin ? (
          <label className="adoption__toggle">
            <input
              type="checkbox"
              checked={allParishes}
              onChange={(e) => setAllParishes(e.target.checked)}
            />
            <span>All parishes</span>
          </label>
        ) : null}
        <button type="button" className="sign-in__link" onClick={exportCsv} disabled={!data}>
          Export CSV
        </button>
      </div>

      {loading ? <Spinner label="Loading adoption numbers" /> : null}
      {error ? <p className="placeholder">Couldn't load adoption numbers: {error}</p> : null}

      {data?.notMigrated ? (
        <section className="panel">
          <p className="panel__empty">
            Adoption tracking isn't switched on yet. Apply migration 0013 and numbers will start
            appearing as volunteers use the portal.
          </p>
        </section>
      ) : null}

      {data && !data.notMigrated && data.parishes.length === 0 ? (
        <section className="panel">
          <p className="panel__empty">
            No confirmed volunteer accounts yet. Numbers appear once volunteers finish signing up.
          </p>
        </section>
      ) : null}

      {totals ? (
        <section className="panel adoption__totals">
          <h2 className="dashboard__section-label">All parishes</h2>
          <p className="adoption__headline">
            {totals.returnRatePct === null ? "—" : `${totals.returnRatePct}%`}
            <span className="adoption__headline-label">came back after their first week</span>
          </p>
          <p className="adoption__detail">
            {totals.returned} of {totals.eligible} volunteers who signed up more than 7 days ago,
            across {data?.parishes.length} parishes ({totals.signedUp} accounts total,{" "}
            {totals.subRequests} sub-requests).
          </p>
        </section>
      ) : null}

      {data?.parishes.map((p) => (
        <section className="panel adoption__parish" key={p.parishId}>
          <h2 className="dashboard__section-label">{p.parishName}</h2>

          <p className="adoption__headline">
            {p.returnRatePct === null ? "—" : `${p.returnRatePct}%`}
            <span className="adoption__headline-label">came back after their first week</span>
          </p>
          <p className="adoption__detail">
            {p.returnEligible === 0
              ? "Nobody has passed the 7-day mark yet, so there's no return rate to show. This fills in as accounts age."
              : `${p.returned} of ${p.returnEligible} volunteers who signed up more than 7 days ago opened the portal again on their own.`}
          </p>

          <dl className="adoption__grid">
            <Stat label="Signed up" value={p.signedUp} note={`${p.newSignups} in this window`} />
            <Stat
              label="Did more than confirm"
              value={p.activatedPct === null ? "—" : `${p.activatedPct}%`}
              note={`${p.activated} of ${p.signedUp}`}
            />
            <Stat
              label="Viewed their schedule"
              value={p.schedulePct === null ? "—" : `${p.schedulePct}%`}
              note={`${p.scheduleEngaged} of ${p.signedUp}`}
            />
            <Stat label="Sub-requests started" value={p.subRequests} note="in this window" />
            <Stat label="Help center opened" value={p.helpUses} note="in this window" />
          </dl>
        </section>
      ))}

      {data && !data.notMigrated ? (
        <p className="settings__section-lede adoption__footnote">
          Counted over the last {data.windowDays} days. "Signed up" is all-time. A volunteer counts
          as returning only if they opened the portal more than 7 days after their own signup, so
          signup-day activity never inflates it.
        </p>
      ) : null}
    </main>
  );
}

function Stat({
  label,
  value,
  note,
}: {
  label: string;
  value: string | number;
  note: string;
}) {
  return (
    <div className="adoption__stat">
      <dt className="adoption__stat-label">{label}</dt>
      <dd className="adoption__stat-value">{value}</dd>
      <span className="adoption__stat-note">{note}</span>
    </div>
  );
}
